import styled from '@emotion/styled';
import { AnimatePresence } from 'framer-motion';
import { Switch, Route, useRouteMatch, useLocation, useHistory } from 'react-router-dom';

import { SCOPES } from '../../../lib/scopes';
import { RouteManager } from '../../../RouteManager';

import { useI18n } from '../../../hooks/useI18nFormatters';
import { useApi } from '../../../store/SelectedHomeyStore.hooks.ts';
import { useUsersById } from '../../../store/users/useUsers.ts';

import { su } from '../../../theme/functions/su';
import { glyph } from '../../../theme/glyph';
import { theme } from '../../../theme/theme';

import { OutlineButton } from '../../../components/buttons/OutlineButton';
import { RoundIconWrapper } from '../../../components/common/RoundIconWrapper';
import { Throbber } from '../../../components/common/Throbber';

import { FamilySettingsDialogCreateInvite } from './FamilySettingsDialogCreateInvite';
import { FamilySettingsDialogDeleteUser } from './FamilySettingsDialogDeleteUser';
import { FamilySettingsDialogDisableLocalUser } from './FamilySettingsDialogDisableLocalUser.jsx';
import { FamilySettingsDialogLocalUser } from './FamilySettingsDialogLocalUser.jsx';
import { FamilySettingsDialogLocalUserUnsupported } from './FamilySettingsDialogLocalUserUnsupported.jsx';
import { FamilySettingsDialogSwapOwner } from './FamilySettingsDialogSwapOwner';
import { FamilySettingsTableInvites } from './FamilySettingsTableInvites';
import { FamilySettingsTableUsers } from './FamilySettingsTableUsers';

export function FamilySettings() {
  const { i18n } = useI18n();
  const { scopes } = useApi();
  const users = useUsersById();

  // Matches the parent route.
  const routeMatch = useRouteMatch();
  const location = useLocation();
  const history = useHistory();

  if (users.isLoading === true) {
    return (
      <S.Root>
        <Throbber size={theme.icon.size_default} />
      </S.Root>
    );
  }

  return (
    <S.Root>
      <FamilySettingsTableUsers />

      <FamilySettingsTableInvites />

      {scopes[SCOPES.USER] === true && (
        <OutlineButton
          styleIconColorDisabled={true}
          onPress={() => {
            history.push(`${location.pathname}/invite`);
          }}
        >
          <RoundIconWrapper size={theme.icon.size_default} color={theme.color.blue}>
            <OutlineButton.Icon
              source={glyph.core.plusMedium}
              size={theme.icon.size_small}
              color={theme.color.white}
            />
          </RoundIconWrapper>
          <OutlineButton.Text>
            {i18n.messageFormatter('settings.system.usersInviteButtonLabel')}
          </OutlineButton.Text>
        </OutlineButton>
      )}

      <AnimatePresence mode="wait">
        <Switch location={location} key={location.pathname}>
          <Route
            path={`${routeMatch.path}/invite`}
            render={() => {
              function handleCloseRequest() {
                RouteManager.toPath(`${routeMatch.url}`);
              }

              return <FamilySettingsDialogCreateInvite onCloseRequest={handleCloseRequest} />;
            }}
          />

          <Route
            path={`${routeMatch.path}/:userId/delete`}
            render={() => {
              function handleCloseRequest() {
                RouteManager.toPath(`${routeMatch.url}`);
              }

              return <FamilySettingsDialogDeleteUser onCloseRequest={handleCloseRequest} />;
            }}
          />

          <Route
            path={`${routeMatch.path}/:userId/swap-owner`}
            render={() => {
              function handleCloseRequest() {
                RouteManager.toPath(`${routeMatch.url}`);
              }

              return <FamilySettingsDialogSwapOwner onCloseRequest={handleCloseRequest} />;
            }}
          />

          <Route
            path={`${routeMatch.path}/:userId/local-user-enable`}
            render={() => {
              function handleCloseRequest() {
                RouteManager.toPath(`${routeMatch.url}`);
              }

              return <FamilySettingsDialogLocalUser onCloseRequest={handleCloseRequest} />;
            }}
          />

          <Route
            path={`${routeMatch.path}/:userId/local-user-edit`}
            render={() => {
              function handleCloseRequest() {
                RouteManager.toPath(`${routeMatch.url}`);
              }

              return (
                <FamilySettingsDialogLocalUser onCloseRequest={handleCloseRequest} view="edit" />
              );
            }}
          />

          <Route
            path={`${routeMatch.path}/:userId/local-user-unsupported`}
            render={() => {
              function handleCloseRequest() {
                RouteManager.toPath(`${routeMatch.url}`);
              }

              return (
                <FamilySettingsDialogLocalUserUnsupported onCloseRequest={handleCloseRequest} />
              );
            }}
          />

          <Route
            path={`${routeMatch.path}/:userId/local-user-disable`}
            render={() => {
              function handleCloseRequest() {
                RouteManager.toPath(`${routeMatch.url}`);
              }

              return <FamilySettingsDialogDisableLocalUser onCloseRequest={handleCloseRequest} />;
            }}
          />
        </Switch>
      </AnimatePresence>
    </S.Root>
  );
}

FamilySettings.pathKey = 'system/family';
FamilySettings.icon = glyph.system.family;
FamilySettings.backgroundColor = theme.color.system_family;

function S() {}
FamilySettings.S = S;

S.Root = styled.div`
  position: relative;
  display: flex;
  flex-direction: column;
  align-items: center;
  padding-top: ${su(3)};

  margin-bottom: ${su(2)};
  flex: 1 0 auto;

  ${Throbber.S.Root} {
    position: absolute;
    left: 50%;
    top: 50%;
    transform: translate(-50%, -50%);
  }

  ${OutlineButton.Root} {
    margin: ${su(2)} 0;
    width: min(calc(100% - ${su(6)}), 720px);
  }
`;
